/**
 * Formular für Kalendertermine (Erstellen/Bearbeiten/Löschen), sowie eine
 * schreibgeschützte Detailansicht für Benutzer ohne "calendar.manage".
 * Löschen erfordert eine zweistufige Bestätigung (eigenes Modal statt
 * window.confirm(), analog zu "Neue Woche" in der Wochenabgabe - siehe
 * dortigen Kommentar zu inkonsistentem Verhalten nativer Browser-Dialoge).
 */
import { useState, type FormEvent } from "react";
import { api, ApiError } from "../../api/client";
import { Modal } from "../../components/ui/Modal";
import type { CalendarEvent } from "../../types";

const COLOR_OPTIONS: { value: string; label: string }[] = [
  { value: "#9c998c", label: "Silber" },
  { value: "#c3ab7f", label: "Sand" },
  { value: "#5c6b2e", label: "Oliv" },
  { value: "#b56a24", label: "Orange" },
  { value: "#a3282a", label: "Rot" },
  { value: "#3b7dc4", label: "Blau" },
  { value: "#7a52c7", label: "Violett" },
  { value: "#2f9e6e", label: "Grün" },
];

interface Props {
  event: CalendarEvent | null; // null = neuer Termin
  defaultDate?: string;         // vorausgewähltes Datum beim Neuanlegen (aus dem angeklickten Kalendertag)
  readOnly?: boolean;           // ohne "calendar.manage": nur Ansicht, kein Bearbeiten/Löschen
  onClose: () => void;
  onSaved: () => void;
}

export function EventForm({ event, defaultDate, readOnly = false, onClose, onSaved }: Props) {
  const isEdit = !!event;
  const [title, setTitle] = useState(event?.title ?? "");
  const [eventDate, setEventDate] = useState(event?.event_date ?? defaultDate ?? "");
  const [startTime, setStartTime] = useState(event?.start_time ?? "");
  const [endTime, setEndTime] = useState(event?.end_time ?? "");
  const [description, setDescription] = useState(event?.description ?? "");
  const [color, setColor] = useState(event?.color ?? COLOR_OPTIONS[0]!.value);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (endTime && endTime <= startTime) {
      setError("Die Endzeit muss nach der Startzeit liegen.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        title, eventDate, startTime,
        endTime: endTime || undefined,
        description: description || undefined,
        color,
      };
      if (isEdit) {
        await api.put(`/api/calendar/events/${event!.id}`, payload);
      } else {
        await api.post("/api/calendar/events", payload);
      }
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Speichern fehlgeschlagen.");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDelete() {
    if (!event) return;
    setSubmitting(true);
    setError(null);
    try {
      await api.delete(`/api/calendar/events/${event.id}`);
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Löschen fehlgeschlagen.");
      setSubmitting(false);
    }
  }

  const formattedDate = (d: string) => new Date(`${d}T00:00:00`).toLocaleDateString("de-DE", { weekday: "long", day: "2-digit", month: "long", year: "numeric" });

  if (readOnly && event) {
    return (
      <Modal title="Termin" onClose={onClose} footer={<button className="btn-secondary" onClick={onClose}>Schließen</button>}>
        <div className="space-y-3 text-sm">
          <div className="flex items-center gap-2.5">
            <span className="w-3.5 h-3.5 rounded-full shrink-0 border border-black/30" style={{ backgroundColor: event.color }} />
            <span className="text-gray-100 font-semibold text-base">{event.title}</span>
          </div>
          <p className="text-gray-400">
            {formattedDate(event.event_date)} · {event.start_time}
            {event.end_time ? ` – ${event.end_time}` : ""} Uhr
          </p>
          {event.description && <p className="text-gray-300 whitespace-pre-wrap border-t border-surface-border pt-3">{event.description}</p>}
        </div>
      </Modal>
    );
  }

  if (confirmingDelete) {
    return (
      <Modal
        title="Termin löschen"
        onClose={() => !submitting && setConfirmingDelete(false)}
        footer={
          <>
            <button className="btn-secondary" onClick={() => setConfirmingDelete(false)} disabled={submitting}>Abbrechen</button>
            <button className="btn-danger" onClick={handleDelete} disabled={submitting}>
              {submitting ? "Wird gelöscht…" : "Endgültig löschen"}
            </button>
          </>
        }
      >
        <div className="space-y-3">
          {error && <div className="bg-danger-muted text-danger text-sm rounded-lg px-3 py-2">{error}</div>}
          <p className="text-gray-200 text-sm">
            Termin <span className="font-semibold text-gray-100">"{event?.title}"</span> am{" "}
            {event && formattedDate(event.event_date)} wirklich unwiderruflich löschen?
          </p>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      title={isEdit ? "Termin bearbeiten" : "Neuer Termin"}
      onClose={onClose}
      footer={
        <>
          {isEdit && (
            <button type="button" className="btn-danger mr-auto" onClick={() => setConfirmingDelete(true)} disabled={submitting}>
              Löschen
            </button>
          )}
          <button className="btn-secondary" onClick={onClose} disabled={submitting}>Abbrechen</button>
          <button className="btn-primary" form="calendar-event-form" disabled={submitting}>
            {submitting ? "Speichern…" : "Speichern"}
          </button>
        </>
      }
    >
      <form id="calendar-event-form" onSubmit={handleSubmit} className="space-y-4">
        {error && <div className="bg-danger-muted text-danger text-sm rounded-lg px-3 py-2">{error}</div>}

        <div>
          <label className="label">Titel *</label>
          <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} required autoFocus />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="label">Datum *</label>
            <input className="input" type="date" value={eventDate} onChange={(e) => setEventDate(e.target.value)} required />
          </div>
          <div>
            <label className="label">Startzeit *</label>
            <input className="input" type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} required />
          </div>
          <div>
            <label className="label">Endzeit</label>
            <input className="input" type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} />
          </div>
        </div>

        <div>
          <label className="label">Beschreibung / Notiz</label>
          <textarea className="input" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} maxLength={2000} />
        </div>

        <div>
          <label className="label">Farbe</label>
          <div className="flex flex-wrap gap-2.5">
            {COLOR_OPTIONS.map((c) => (
              <button
                key={c.value}
                type="button"
                title={c.label}
                aria-label={c.label}
                onClick={() => setColor(c.value)}
                className={`w-8 h-8 rounded-full border-2 transition-transform ${
                  color === c.value ? "border-gray-100 scale-110" : "border-black/30 hover:scale-105"
                }`}
                style={{ backgroundColor: c.value }}
              />
            ))}
          </div>
        </div>
      </form>
    </Modal>
  );
}
